import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useEarningsVisibility } from "../contexts/EarningsVisibilityContext";
import { dashboardRequest } from "../services/dashboardRequest";
import { useDashboardResource } from "../hooks/useDashboardResource";
import { statisticsTimezone } from "../utils/frameStatisticsDate";
import { DEFAULT_CURRENCY, formatMoney as formatCurrency } from "../utils/currency";
import "../assets/css/ClubDashboard.css";

const GAME_TYPES = ["snooker", "pool", "us-pool", "chinese-pool"];

const TYPE_LABELS = {
    snooker: "Snooker",
    pool: "Pool",
    "us-pool": "US pool",
    "chinese-pool": "Heyball",
};

const TYPE_TONES = {
    snooker: "mint",
    pool: "blue",
    "us-pool": "lilac",
    "chinese-pool": "sand",
};

const WEEK_DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const getEmptyDashboardData = () => ({
    stats: null,
    tables: [],
    players: [],
    queues: [],
    games: [],
    finishedGames: [],
    settings: {},
});

const DashboardIcon = ({ name }) => {
    const common = {
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        strokeWidth: "1.8",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        "aria-hidden": true,
        focusable: "false",
    };

    switch (name) {
        case "wallet":
            return <svg {...common}><path d="M4 7.5h14a2 2 0 0 1 2 2v8H5a2 2 0 0 1-2-2v-10a2 2 0 0 1 2-2h12" /><path d="M15 11h5v4h-5a2 2 0 1 1 0-4Z" /></svg>;
        case "frames":
            return <svg {...common}><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="3" /><path d="M12 3.5v2M20.5 12h-2M12 20.5v-2M3.5 12h2" /></svg>;
        case "tables":
            return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="3" /><circle cx="7" cy="9" r="1" /><circle cx="17" cy="9" r="1" /><circle cx="7" cy="15" r="1" /><circle cx="17" cy="15" r="1" /></svg>;
        case "queue":
            return <svg {...common}><circle cx="8" cy="8" r="3" /><path d="M3 19a5 5 0 0 1 10 0M16 5h5M16 9h5M16 13h5M16 17h5" /></svg>;
        case "users":
            return <svg {...common}><circle cx="9" cy="8" r="3" /><path d="M4 19a5 5 0 0 1 10 0M16 8a2.5 2.5 0 0 1 0 5M17 16a4 4 0 0 1 3 3" /></svg>;
        case "trophy":
            return <svg {...common}><path d="M8 4h8v5a4 4 0 0 1-8 0V4ZM10 17h4M12 13v4M8 6H5v2a3 3 0 0 0 3 3M16 6h3v2a3 3 0 0 1-3 3M8 20h8" /></svg>;
        case "refresh":
            return <svg {...common}><path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4" /><path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" /></svg>;
        case "eye":
            return <svg {...common}><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" /><circle cx="12" cy="12" r="2.5" /></svg>;
        case "eye-off":
            return <svg {...common}><path d="m3 3 18 18M10.6 6.1A9.4 9.4 0 0 1 12 6c6 0 9.5 6 9.5 6a16 16 0 0 1-2.1 2.8M6.6 6.7C4 8.3 2.5 12 2.5 12s3.5 6 9.5 6a9 9 0 0 0 3.1-.5M9.9 9.9a3 3 0 0 0 4.2 4.2" /></svg>;
        case "arrow":
            return <svg {...common}><path d="M5 12h14M14 7l5 5-5 5" /></svg>;
        case "check":
            return <svg {...common}><path d="m5 12 4 4L19 6" /></svg>;
        case "clock":
            return <svg {...common}><circle cx="12" cy="12" r="8.5" /><path d="M12 7v5l3 2" /></svg>;
        case "game":
            return <svg {...common}><path d="M7 8h10a4 4 0 0 1 3.7 5.5l-1 2.5a2.3 2.3 0 0 1-3.8.7L14 15H10l-1.9 1.7a2.3 2.3 0 0 1-3.8-.7l-1-2.5A4 4 0 0 1 7 8Z" /><path d="M8 11v3M6.5 12.5h3M16.5 11.5h.01M18 13h.01" /></svg>;
        default:
            return <svg {...common}><circle cx="12" cy="12" r="8.5" /><path d="M12 8v4M12 16h.01" /></svg>;
    }
};

const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
};

const getClubDisplayName = (user) => {
    const rawName = String(user?.name || "your club").trim();
    const shortName = rawName.includes("@") ? rawName.split("@")[0] : rawName;
    return shortName.replace(/[._-]+/g, " ").replace(/\b\w/g, character => character.toUpperCase());
};

const formatDate = () => new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
}).format(new Date());

const formatGameType = (type) => TYPE_LABELS[type] || String(type || "Game").replace(/-/g, " ");

const getInitials = (name) => String(name || "Player")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join("");

const ClubDashboard = ({ user }) => {
    const { showEarnings, toggleEarningsVisibility } = useEarningsVisibility();
    const loadDashboard = useCallback(async ({ signal }) => {
        const [overview, tables, players, queues, settings, games, finishedGames] = await Promise.all([
            dashboardRequest(`/club/overview?timezone=${encodeURIComponent(statisticsTimezone())}`, { signal }),
            dashboardRequest("/club/tables", { method: "POST", body: { id: user.id }, signal }),
            dashboardRequest("/club/players", { method: "POST", body: { id: user.id, include_default: true }, signal }),
            dashboardRequest(`/public/tables/queues/${user.id}`, { signal }),
            dashboardRequest(`/settings/${user.id}`, { signal }),
            dashboardRequest("/club/games", { method: "POST", body: { id_user: user.id }, signal }),
            dashboardRequest("/club/finished-games", { method: "POST", body: { id_user: user.id }, signal }),
        ]);
        if (!overview?.stats || typeof overview.stats !== "object" || Array.isArray(overview.stats)
            || !Array.isArray(tables?.tables) || !Array.isArray(players?.players)
            || !Array.isArray(queues?.tables) || !Array.isArray(games?.games)
            || !Array.isArray(finishedGames?.games) || !settings?.settings
            || typeof settings.settings !== "object" || Array.isArray(settings.settings)) {
            throw new Error("The club overview response was incomplete. Please try again.");
        }
        return {
            stats: overview.stats, tables: tables.tables, players: players.players,
            queues: queues.tables, settings: settings.settings, games: games.games,
            finishedGames: finishedGames.games, updatedAt: new Date(),
        };
    }, [user?.id]);
    const { data, loading, refreshing, error, refresh } = useDashboardResource({
        key: `club-overview:${user?.id || ""}`, enabled: Boolean(user?.id), load: loadDashboard,
    });
    const dashboardData = data || getEmptyDashboardData();
    const lastUpdated = data?.updatedAt;
    const notice = error ? (data
        ? "The overview could not be refreshed. Showing the last successful update; please retry."
        : "We couldn't load your club overview. Check the connection and try again.") : "";

    const summary = useMemo(() => {
        const stats = dashboardData.stats || {};
        const enabledTables = dashboardData.tables.filter(table => table.deleted_at === null);
        const disabledTables = dashboardData.tables.filter(table => table.deleted_at !== null);
        const linkedTables = enabledTables.filter(table => table.current_game_id);
        const readyTables = enabledTables.filter(table => !table.current_game_id);
        const queueEntries = dashboardData.queues.flatMap(table =>
            (table.queues || []).map(queue => ({
                ...queue,
                tableId: table.table_id,
                tableName: table.table_name,
                tableType: table.table_type,
            }))
        );
        const todayRows = GAME_TYPES.map(type => stats.frames_today?.[type] || { count: 0, revenue: 0 });

        return {
            enabledTables,
            disabledTables,
            linkedTables,
            readyTables,
            queueEntries,
            activeQueueTables: dashboardData.queues.filter(table => table.queues?.length).length,
            todayFrames: todayRows.reduce((total, row) => total + Number(row.count || 0), 0),
            todayQualifiedFrames: todayRows.reduce((total, row) => total + Number(row.qualified_count || 0), 0),
            todayNotQualifiedFrames: todayRows.reduce((total, row) => total + Number(row.not_qualified_count || 0), 0),
            todayRevenue: todayRows.reduce((total, row) => total + Number(row.revenue || 0), 0),
            registeredPlayers: dashboardData.players.filter(player => player.deleted_at === null).length,
        };
    }, [dashboardData]);

    const weeklyActivity = useMemo(() => {
        const rows = Array.isArray(dashboardData.stats?.frames_week_daily)
            ? dashboardData.stats.frames_week_daily
            : [];
        const todayIndex = (new Date().getDay() + 6) % 7;

        return WEEK_DAY_LABELS.map((label, index) => {
            const row = rows[index] || {};
            return {
                date: row.date || "",
                label: row.label || label,
                count: Number(row.count || 0),
                isCurrent: typeof row.is_current === "boolean" ? row.is_current : index === todayIndex,
                isFuture: typeof row.is_future === "boolean" ? row.is_future : index > todayIndex,
            };
        });
    }, [dashboardData.stats]);

    const weeklyFrames = weeklyActivity.reduce((total, day) => total + day.count, 0);
    const weeklyRevenue = GAME_TYPES.reduce(
        (total, type) => total + Number(dashboardData.stats?.frames_week?.[type]?.revenue || 0),
        0
    );
    const maxWeeklyFrames = Math.max(1, ...weeklyActivity.map(day => day.count));
    const roomActivity = [
        { key: "linked", label: "In play", count: summary.linkedTables.length, tone: "is-live" },
        { key: "queues", label: "Queues", count: summary.activeQueueTables, tone: "is-queue" },
        { key: "ready", label: "Ready", count: summary.readyTables.length, tone: "is-ready" },
    ];
    const maxRoomActivity = Math.max(1, ...roomActivity.map(item => item.count));
    const currency = dashboardData.settings.default_currency || DEFAULT_CURRENCY;
    const activeTablesToShow = summary.enabledTables.slice(0, 6);
    const gamesById = useMemo(() => new Map(dashboardData.games.map(game => [Number(game.id), game])), [dashboardData.games]);
    const bestBreak = dashboardData.stats?.highest_break || { value: 0, player: null };
    const clubName = getClubDisplayName(user);

    const formatMoney = (amount) => {
        if (!showEarnings) return "••••";
        // Headline takings read better without the subunit, whatever the currency.
        return formatCurrency(amount, currency, { decimals: 0 });
    };

    return (
        <main className="club-dashboard" aria-busy={loading || refreshing}>
            <div className="club-dashboard-glow club-dashboard-glow-left" aria-hidden="true" />
            <div className="club-dashboard-glow club-dashboard-glow-right" aria-hidden="true" />

            <div className="club-dashboard-container">
                <section className="club-dashboard-welcome" aria-labelledby="club-dashboard-title">
                    <div className="club-dashboard-welcome-copy">
                        <span className="club-dashboard-eyebrow"><i /> Club overview</span>
                        <h1 id="club-dashboard-title">{getGreeting()}, {clubName}.</h1>
                        <p>{formatDate()} · Here is the clearest view of your club right now.</p>
                        {(loading || refreshing) && <span className="club-dashboard-syncing" role="status"><i /> {data ? "Refreshing live club data…" : "Loading live club data…"}</span>}
                    </div>
                    <div className="club-dashboard-actions">
                        <button
                            type="button"
                            className="club-dashboard-refresh"
                            onClick={refresh}
                            disabled={!user?.id || loading || refreshing}
                        >
                            <DashboardIcon name="refresh" />
                            <span>{refreshing ? "Refreshing" : "Refresh"}</span>
                        </button>
                        <Link to="/games" className="club-dashboard-primary-action">
                            Manage matches <DashboardIcon name="arrow" />
                        </Link>
                    </div>
                </section>

                {notice && <div className="club-dashboard-notice" role="alert"><DashboardIcon name="info" /><span>{notice}</span></div>}

                {data && <>
                <section className="club-dashboard-metrics" aria-label="Today's club summary">
                    <article className="club-metric-card club-metric-card-featured">
                        <div className="club-metric-card-top">
                            <span className="club-metric-icon"><DashboardIcon name="wallet" /></span>
                            <button
                                type="button"
                                className="club-earnings-toggle"
                                onClick={toggleEarningsVisibility}
                                aria-label={showEarnings ? "Hide estimated takings" : "Show estimated takings"}
                                title={showEarnings ? "Hide estimated takings" : "Show estimated takings"}
                            >
                                <DashboardIcon name={showEarnings ? "eye" : "eye-off"} />
                            </button>
                        </div>
                        <span className="club-metric-label">Estimated takings today</span>
                        <strong>{formatMoney(summary.todayRevenue)}</strong>
                        <small>Qualified paid frames × current club prices</small>
                    </article>

                    <article className="club-metric-card" aria-label="Recorded frames">
                        <span className="club-metric-icon club-tone-blue"><DashboardIcon name="frames" /></span>
                        <span className="club-metric-label">Recorded frames</span>
                        <strong>{summary.todayQualifiedFrames}</strong>
                        <small>{summary.todayFrames} recorded · {summary.todayNotQualifiedFrames} not qualified</small>
                    </article>

                    <article className="club-metric-card">
                        <span className="club-metric-icon club-tone-mint"><DashboardIcon name="tables" /></span>
                        <span className="club-metric-label">Enabled tables</span>
                        <strong>{summary.enabledTables.length}</strong>
                        <small>{summary.linkedTables.length} with a match linked</small>
                    </article>

                    <article className="club-metric-card">
                        <span className="club-metric-icon club-tone-lilac"><DashboardIcon name="queue" /></span>
                        <span className="club-metric-label">Players waiting</span>
                        <strong>{summary.queueEntries.length}</strong>
                        <small>Across {dashboardData.queues.filter(table => table.queues?.length).length} table queues</small>
                    </article>
                </section>

                <section className="club-dashboard-overview-grid">
                    <article className="club-dashboard-panel club-weekly-insight" aria-labelledby="club-weekly-activity-title">
                        <div className="club-panel-heading">
                            <div>
                                <span className="club-panel-kicker">Club activity</span>
                                <h2 id="club-weekly-activity-title">Weekly activity</h2>
                            </div>
                            <span className="club-insight-live"><i /> Live data</span>
                        </div>

                        <div className="club-insight-summary">
                            <div><strong>{weeklyFrames}</strong><span>recorded frames this week</span></div>
                            <div><strong>{formatMoney(weeklyRevenue)}</strong><span>estimated takings</span></div>
                        </div>

                        <div className="club-weekly-chart" role="list" aria-label={`${weeklyFrames} frames recorded from Monday to Sunday`}>
                            {weeklyActivity.map(day => (
                                <div
                                    className={`club-weekly-bar${day.isCurrent ? " is-current" : ""}${day.isFuture ? " is-future" : ""}`}
                                    key={day.date || day.label}
                                    role="listitem"
                                    aria-label={`${day.label}: ${day.count} ${day.count === 1 ? "frame" : "frames"}`}
                                    title={`${day.label}: ${day.count} ${day.count === 1 ? "frame" : "frames"}`}
                                >
                                    <strong>{day.count}</strong>
                                    <span className="club-weekly-bar-track" aria-hidden="true">
                                        <i style={{ height: `${day.count ? Math.max(14, (day.count / maxWeeklyFrames) * 100) : 7}%` }} />
                                    </span>
                                    <small>{day.label}</small>
                                </div>
                            ))}
                        </div>

                        <p className="club-insight-note">Every frame recorded from Monday through Sunday. Today is highlighted.</p>
                    </article>

                    <article className="club-dashboard-panel club-room-insight" aria-labelledby="club-room-activity-title">
                        <div className="club-panel-heading">
                            <div>
                                <span className="club-panel-kicker">Live floor</span>
                                <h2 id="club-room-activity-title">Room activity</h2>
                            </div>
                            <span className="club-insight-now"><i /> Now</span>
                        </div>

                        <div className="club-room-chart" role="list" aria-label="Current room activity">
                            {roomActivity.map(item => (
                                <div className={`club-room-bar ${item.tone}`} key={item.key} role="listitem" aria-label={`${item.label}: ${item.count}`}>
                                    <strong>{item.count}</strong>
                                    <span className="club-room-bar-track" aria-hidden="true">
                                        <i style={{ height: `${item.count ? Math.max(14, (item.count / maxRoomActivity) * 100) : 7}%` }} />
                                    </span>
                                    <small>{item.label}</small>
                                </div>
                            ))}
                        </div>

                        <dl className="club-floor-facts">
                            <div><dt><i className="is-ready" />Enabled tables</dt><dd>{summary.enabledTables.length}</dd></div>
                            <div><dt><i className="is-disabled" />Disabled tables</dt><dd>{summary.disabledTables.length}</dd></div>
                            <div><dt><DashboardIcon name="users" />Registered players</dt><dd>{summary.registeredPlayers}</dd></div>
                        </dl>

                        {Number(bestBreak.value || 0) > 0 && (
                            <div className="club-record-note">
                                <span><DashboardIcon name="trophy" /></span>
                                <div><small>Recorded club break</small><strong>{bestBreak.value} <em>by {bestBreak.player || "Unknown player"}</em></strong></div>
                            </div>
                        )}
                    </article>
                </section>

                <section className="club-dashboard-section club-live-floor-section" aria-labelledby="club-tables-title">
                    <span className="club-live-floor-orbit" aria-hidden="true" />
                    <div className="club-section-heading club-live-floor-heading">
                        <div>
                            <span className="club-panel-kicker club-live-floor-kicker"><i /> Live floor</span>
                            <h2 id="club-tables-title">Every enabled table, one clean view</h2>
                            <p>See what is in play, who is waiting, and where your team should look next.</p>
                        </div>
                        <Link to="/club" className="club-section-link">Manage tables <DashboardIcon name="arrow" /></Link>
                    </div>

                    <dl className="club-live-floor-summary" aria-label="Current floor summary">
                        <div><dt>Enabled tables</dt><dd>{summary.enabledTables.length}</dd></div>
                        <div><dt>Matches linked</dt><dd>{summary.linkedTables.length}</dd></div>
                        <div><dt>Players waiting</dt><dd>{summary.queueEntries.length}</dd></div>
                    </dl>

                    {activeTablesToShow.length > 0 ? (
                        <div className="club-table-overview-grid" role="list" aria-label="Enabled table status">
                            {activeTablesToShow.map((table, index) => {
                                const linkedGame = table.current_game_id ? gamesById.get(Number(table.current_game_id)) : null;
                                const tableQueue = dashboardData.queues.find(item => Number(item.table_id) === Number(table.id))?.queues || [];
                                const isLinked = Boolean(table.current_game_id);
                                const tableNumber = String(table.name || "").match(/\d+/)?.[0] || String(index + 1);
                                const destination = isLinked && linkedGame?.link ? `/game/${linkedGame.link}` : `/queues/all/${user.id}`;
                                const actionLabel = isLinked && linkedGame?.link ? "Open match" : "Open queue";

                                return (
                                    <article className={`club-table-overview-card ${isLinked ? "is-linked" : "is-ready"}`} key={table.id} role="listitem">
                                        <span className="club-table-sequence" aria-hidden="true">{String(tableNumber).padStart(2, "0")}</span>
                                        <div className="club-table-overview-main">
                                            <span className={`club-table-type club-tone-${TYPE_TONES[table.type] || "mint"}`}>{formatGameType(table.type)}</span>
                                            <h3>{table.name}</h3>
                                            {linkedGame ? (
                                                <p>{linkedGame.first_player_name} <span>vs</span> {linkedGame.second_player_name}</p>
                                            ) : tableQueue.length > 0 ? (
                                                <p>{tableQueue[0].player_name} <span>is next</span></p>
                                            ) : (
                                                <p>No match currently linked</p>
                                            )}
                                        </div>
                                        <div className="club-table-live-state">
                                            <span className="club-table-status"><i />{isLinked ? "Match linked" : "Ready"}</span>
                                            <small>{isLinked ? "Live table activity" : "Available for the next match"}</small>
                                        </div>
                                        <div className="club-table-queue-count" aria-label={`${tableQueue.length} waiting at ${table.name}`}>
                                            <DashboardIcon name="queue" />
                                            <span><strong>{tableQueue.length}</strong><small>waiting</small></span>
                                        </div>
                                        <Link className="club-table-row-action" to={destination} aria-label={`${actionLabel} for ${table.name}`}>
                                            <span>{actionLabel}</span><DashboardIcon name="arrow" />
                                        </Link>
                                    </article>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="club-dashboard-empty-card">
                            <span><DashboardIcon name="tables" /></span>
                            <div><h3>No enabled tables yet</h3><p>Once a table is enabled, its room status will appear here.</p></div>
                            <Link to="/club">Open tables</Link>
                        </div>
                    )}
                </section>

                <section className="club-dashboard-operations-grid">
                    <article className="club-dashboard-panel club-queue-snapshot">
                        <div className="club-panel-heading">
                            <div>
                                <span className="club-panel-kicker">Waiting list</span>
                                <h2>Who is up next</h2>
                            </div>
                            <Link to={`/queues/all/${user.id}`} className="club-panel-link" aria-label="Manage all queues"><DashboardIcon name="arrow" /></Link>
                        </div>

                        {summary.queueEntries.length > 0 ? (
                            <ol className="club-queue-list">
                                {summary.queueEntries.slice(0, 4).map((queue, index) => (
                                    <li key={`${queue.tableId}-${queue.id}`}>
                                        <span className="club-queue-position">{String(index + 1).padStart(2, "0")}</span>
                                        <span className={`club-player-avatar club-tone-${TYPE_TONES[queue.tableType] || "mint"}`}>{getInitials(queue.player_name)}</span>
                                        <div><strong>{queue.player_name}</strong><span>{queue.tableName} · position {queue.position}</span></div>
                                        <DashboardIcon name="clock" />
                                    </li>
                                ))}
                            </ol>
                        ) : (
                            <div className="club-panel-empty">
                                <span><DashboardIcon name="queue" /></span>
                                <h3>No one is waiting</h3>
                                <p>Your table queues are clear right now.</p>
                            </div>
                        )}
                    </article>

                    <article className="club-dashboard-panel club-recent-results">
                        <div className="club-panel-heading">
                            <div>
                                <span className="club-panel-kicker">Recent results</span>
                                <h2>Latest finished matches</h2>
                            </div>
                            <Link to="/games/finished" className="club-panel-link" aria-label="View finished matches"><DashboardIcon name="arrow" /></Link>
                        </div>

                        {dashboardData.finishedGames.length > 0 ? (
                            <div className="club-results-list">
                                {dashboardData.finishedGames.slice(0, 4).map(game => (
                                    <Link to={game.link ? `/game/${game.link}` : "/games/finished"} key={game.id}>
                                        <span className={`club-result-icon club-tone-${TYPE_TONES[game.type] || "mint"}`}><DashboardIcon name="game" /></span>
                                        <div>
                                            <strong>{game.first_player_name} <span>vs</span> {game.second_player_name}</strong>
                                            <small>{game.table_name} · {formatGameType(game.type)}</small>
                                        </div>
                                        <DashboardIcon name="arrow" />
                                    </Link>
                                ))}
                            </div>
                        ) : (
                            <div className="club-panel-empty">
                                <span><DashboardIcon name="game" /></span>
                                <h3>No finished matches yet</h3>
                                <p>Your latest completed club matches will appear here.</p>
                            </div>
                        )}
                    </article>
                </section>

                <div className="club-dashboard-footnote">
                    <span><i /> Club data refreshed {lastUpdated ? lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "just now"}</span>
                    <span>Estimated takings use qualified paid frames and your current prices.</span>
                </div>
                </>}
            </div>
        </main>
    );
};

export default ClubDashboard;
